import { useCallback, useDebugValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncEntity } from '../../global';
import { RootState } from '../../store';
import { packagesSelectors, updatePackage } from './companyPackages.state';
import { CompanyPackage } from './companyPackages.types';
import { uploadPackageImage } from './companyPackages.api';
import { useUploadFileToBucket } from '../../hooks/useUploadFileToBucket';
import { AppDispatch } from '../../index';

export const usePackagesState = (): RootState['companyPackages'] => {
  const packages = useSelector<RootState, RootState['companyPackages']>(state => state.companyPackages);

  useDebugValue(packages);

  return packages;
};

export const usePackagesValues = (): CompanyPackage[] => {
  const packages = useSelector<RootState, RootState['companyPackages']>(state => state.companyPackages);

  const result = packagesSelectors.selectAll(packages);

  useDebugValue(result);

  return result;
};

export const usePackageById = (id: string): AsyncEntity<CompanyPackage> | null => {
  const packages = useSelector<RootState, RootState['companyPackages']>(state => state.companyPackages);

  const result = packagesSelectors.selectById(packages, id) || null;

  useDebugValue(result);

  return result;
};

export type UsePackageByCompanyId = (params: { companyId: string }) => CompanyPackage[];

export const usePackageByCompanyId: UsePackageByCompanyId = ({ companyId }) => {
  const state = useSelector<RootState, RootState['companyPackages']>(state => state.companyPackages);
  const companyPackages = useSelector<RootState, string[]>(
    state => state.companyPackages.companySelector[companyId] || []
  );

  const result = companyPackages.reduce((acc, el) => {
    const pkg = packagesSelectors.selectById(state, el);

    if (pkg) {
      acc.push(pkg);
    }

    return acc;
  }, [] as CompanyPackage[]);

  useDebugValue(result);

  return result;
};

export type UsePackageImage = (params: {
  packageId: string;
  companyId: string;
}) => [string, { progress: number; updatePackageImage: (file: File) => void }];

export const usePackageImage: UsePackageImage = ({ packageId, companyId }) => {
  const dispatch: AppDispatch = useDispatch();
  const pkg = usePackageById(packageId);
  const [progress, uploadFile] = useUploadFileToBucket();
  const { image = '' } = pkg || {};

  const updatePackageImage = useCallback(
    async (file: File) => {
      const { name: filename } = file;

      const credentials = await uploadPackageImage({
        packageId,
        companyId,
        data: { filename },
      });

      const { url, fields } = credentials.data;
      const { key } = fields;

      if (credentials.status < 400) {
        const response = await uploadFile({ ...credentials.data, file });

        if (response) {
          dispatch(
            updatePackage({
              companyId,
              packageId,
              data: { image: `${url}/${key}` },
            })
          );
        }
      }
    },
    [companyId, dispatch, packageId, uploadFile]
  );

  return [image, { progress, updatePackageImage }];
};
